import { BlameLogRefType } from '@debbie/common/dist/blamelog/index.js'
import keys from '@debbie/common/dist/cache/keys.js'
import { Case, getJournalNumber } from '@debbie/common/dist/cases/index.js'
import { Customer } from '@debbie/common/dist/customers/index.js'
import { Entity, Site } from '@debbie/common/dist/index.js'
import { isAdmin } from '@debbie/common/dist/permissions/index.js'
import { SearchItem } from '@debbie/common/dist/search/index.js'
import { useMutation } from '@debbie/common/dist/util/hooks.js'
import * as casesAPI from '@debbie/cortex/dist/api/cases.js'
import { caseVouchersEntity } from '@debbie/cortex/dist/case-vouchers/index.js'
import {
  casesEntity,
  ensureCaseLoaded,
} from '@debbie/cortex/dist/cases/index.js'
import { h } from '@debbie/cortex/dist/index.js'
import { interactionsEntity } from '@debbie/cortex/dist/interactions/index.js'
import { useRole } from '@debbie/cortex/dist/me/index.js'
import q from '@debbie/cortex/dist/query.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import {
  ultimateCreditorOptions,
  useUltimateCreditors,
} from '@debbie/cortex/dist/ultimate-creditors/index.js'
import {
  ensureUpdatesLoadedByRef,
  updatesEntity,
} from '@debbie/cortex/dist/updates/index.js'
import { BlameLogsContent } from '@debbie/iris/dist/components/BlameLog/BlameLog.js'
import { Col } from '@debbie/iris/dist/components/Grid/index.js'
import {
  Briefcase,
  File,
  GitMerge,
  Hexagon,
  Link,
  MoreVertical,
  Settings,
  Users,
  X,
  Zap,
} from '@debbie/iris/dist/components/Icons/index.js'
import Pause from '@debbie/iris/dist/components/Icons/Pause.js'
import Trash2 from '@debbie/iris/dist/components/Icons/Trash2.js'
import {
  Button,
  ButtonDrop,
  CasePreview,
  CaseSettings,
  CaseStatusSelector,
  CaseStatus as CaseStatusStyled,
  Errors,
  ExclusiveContent,
  MenuItem,
  Modal,
  PageMenu,
  PlaceholderLoader,
  Search,
  Select,
  Well,
} from '@debbie/iris/dist/components/index.js'
import SearchResponsible from '@debbie/iris/dist/components/SearchResponsible.js'
import Shortcut from '@debbie/iris/dist/components/Shortcut.js'
import WorkflowStatus from '@debbie/iris/dist/components/WorkflowStatus.js'
import { errorNotification } from '@debbie/iris/dist/utils/errors.js'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { styled } from 'styled-components'

import AssociatedCaseModal from './AssociatedCaseModal.js'
import { ActiveUsersOnCase } from './index.js'

type Props = {
  case: Case
  customer: Customer
  id: string
  setShowNewUpdate: (showNewUpdate: boolean) => void
  showNewUpdate: boolean
}

const dropdownStyle: React.CSSProperties = {
  textAlign: 'left',
  textTransform: 'initial',
}

const CaseHeader = (props: Props) => {
  const {
    customer,
    case: singleCase,
    id,
    setShowNewUpdate,
    showNewUpdate,
  } = props

  const [deleteModalOpen, setDeleteModalOpen] = useState(false)

  const [mergeModalOpen, setMergeModalOpen] = useState<boolean>(false)
  const [associatedCaseModalOpen, setAssociatedCaseModalOpen] =
    useState<boolean>(false)
  const [blameLogsOpen, setBlameLogsOpen] = useState<boolean>(false)
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false)
  const [responsibleOpen, setresponsibleOpen] = useState<boolean>(false)
  const [moreOptionsOpen, setMoreOptionsOpen] = useState<boolean>(false)
  const [toCase, setToCase] = useState<SearchItem | null>(null)
  const [ultimateCreditorOpen, setUltimateCreditorOpen] =
    useState<boolean>(false)
  const [selectedUltimateCreditorId, setSelectedUltimateCreditorId] = useState<
    string | null
  >(singleCase.ultimateCreditorId)

  const lang = useSelector((s: RootState) => s.lang)
  const site = useSelector((s: RootState) => s.site)
  const meRole = useRole()

  const responsible =
    singleCase.responsibleType && singleCase.responsibleReferenceId
      ? {
          type: singleCase.responsibleType,
          referenceId: singleCase.responsibleReferenceId,
        }
      : null

  const toggleMergeModalOpen = () => {
    setMergeModalOpen(!mergeModalOpen)
  }
  const toggleAssociatedCaseModalOpen = () => {
    setAssociatedCaseModalOpen(!associatedCaseModalOpen)
  }
  const toggleDeleteModal = () => {
    setDeleteModalOpen(!deleteModalOpen)
  }

  const ultimateCreditorsLoadable = useUltimateCreditors(
    ultimateCreditorOpen ? singleCase.creditorId : null,
  )
  const ultimateCreditors = h.hasData(ultimateCreditorsLoadable)
    ? ultimateCreditorsLoadable.data
    : null

  const ultimateCreditorMutation = useMutation(
    async (ultimateCreditorId: string | null) => {
      await casesAPI.patch(singleCase.caseId, { ultimateCreditorId })
      q.invalidate(keys.write.modify(casesEntity, singleCase.caseId))
    },
    {
      onSuccess: () => {
        setUltimateCreditorOpen(false)
      },
      onError: (err) => {
        errorNotification(lang, err, 'update', lang.errorEntities.case)
      },
    },
  )

  // Mutation for updating case responsible
  const responsibleMutation = useMutation(
    (change: { type: string; referenceId: string | null } | null) =>
      casesAPI.patch(singleCase.caseId, {
        responsibleType: change?.type || null,
        responsibleReferenceId: change?.referenceId || null,
      }),
    {
      onSuccess: () => {
        q.invalidate(keys.write.modify(casesEntity, singleCase.caseId))
      },
      onError: (err) => {
        errorNotification(lang, err, 'update', lang.errorEntities.case)
      },
    },
  )

  // Helper to invalidate and reload case cache
  const reloadCase = async (caseId: string, ensureLoaded: boolean = true) => {
    q.invalidate(keys.write.modify(casesEntity, caseId))
    q.invalidate([
      ...keys.write.create(updatesEntity),
      ...keys.write.create(caseVouchersEntity),
      ...keys.write.create(interactionsEntity),
    ])
    if (ensureLoaded) {
      await ensureCaseLoaded(caseId)
      await ensureUpdatesLoadedByRef(caseId)
    }
  }

  // Mutation for merging cases
  const mergeMutation = useMutation(
    async (toCaseId: string) => {
      await casesAPI.merge(singleCase.caseId, { toCaseId })
      // Reload both case caches
      await reloadCase(singleCase.caseId)
      await reloadCase(toCaseId)
    },
    {
      onSuccess: () => {
        setMergeModalOpen(false)
        setToCase(null)
      },
      onError: (err) => {
        errorNotification(lang, err, 'any', lang.errorEntities.merge)
      },
    },
  )

  // Mutation for deleting case
  const deleteMutation = useMutation(
    async () => {
      await casesAPI.deleteCase(props.case.caseId)
      q.invalidate(keys.write.modify(casesEntity, props.case.caseId))
      await ensureCaseLoaded(props.case.caseId)
    },
    {
      onError: (err) => {
        errorNotification(lang, err, 'delete', lang.errorEntities.case)
      },
    },
  )

  // Mutation for adding case to search index
  const indexMutation = useMutation(
    async () => {
      if (!singleCase) {
        return
      }
      await casesAPI.index(singleCase.caseId)
    },
    {
      onError: (err) => {
        errorNotification(lang, err, 'any', lang.errorEntities.searchIndex)
      },
    },
  )

  const journalNumber = singleCase.customJournalNumber
    ? getJournalNumber(singleCase.customJournalNumber, singleCase.id)
    : `${singleCase.id}`

  return (
    <Col size="md" width="12">
      <PageMenu
        breadCrumb={[
          {
            text: lang.navigation.cases,
            path: '/cases',
          },
          {
            text: journalNumber,
            path: `/cases/${id}`,
            copyToClipBoard: journalNumber,
          },
        ]}
        contentLeft={
          <>
            {customer && singleCase && singleCase.workflowId && (
              <MenuItem>
                <WorkflowStatus case={singleCase} customer={customer} />
              </MenuItem>
            )}
            <ActiveUsersOnCase id={`${singleCase.id}`} />
          </>
        }
        mobileMenu={
          <Button
            size="small"
            className="ui-test-db701363"
            onClick={() => setShowNewUpdate(!showNewUpdate)}
            style={{ marginRight: '10px' }}
          >
            <Pause
              style={{
                margin: '2px 0 -2px',
              }}
            />
          </Button>
        }
        contentRight={
          singleCase && (
            <>
              <MenuItem>
                <CaseStatusSelector case={singleCase} />
              </MenuItem>
              <MenuItem>
                <Shortcut
                  keys={['command', 'k']}
                  onPress={(e: any) => {
                    e.preventDefault()
                    setShowNewUpdate(!showNewUpdate)
                  }}
                  style={{
                    left: '-15px',
                    top: '5px',
                  }}
                />
                <Button
                  size="small"
                  className="ui-test-db701363"
                  onClick={() => setShowNewUpdate(!showNewUpdate)}
                >
                  <Pause
                    style={{
                      margin: '2px 0 -2px',
                    }}
                  />{' '}
                  {lang.cases.pause}
                </Button>
              </MenuItem>
              <MenuItem>
                <CaseStatusStyled
                  status={singleCase.status}
                  statusTime={singleCase.statusTime}
                  endReasonId={singleCase.endReasonId}
                />
              </MenuItem>
              {site === Site.Caseworker && (
                <MenuItem>
                  <ButtonDrop
                    customButton={
                      <MoreVertical
                        style={{
                          width: '16px',
                          height: '16px',
                          marginLeft: '-20px',
                        }}
                      />
                    }
                    closeOnClickOutside
                    open={moreOptionsOpen}
                    onOpen={() => {
                      setMoreOptionsOpen(true)
                    }}
                    onClose={() => {
                      setMoreOptionsOpen(false)
                    }}
                    size="mini"
                  >
                    <Button
                      block
                      onClick={() => setBlameLogsOpen(!blameLogsOpen)}
                      theme="light"
                      style={dropdownStyle}
                    >
                      <File
                        style={{
                          cursor: 'pointer',
                        }}
                      />{' '}
                      {lang.blameLogs.blameLog}
                    </Button>
                    <Button
                      block
                      onClick={() => setresponsibleOpen(!responsibleOpen)}
                      theme="light"
                      style={dropdownStyle}
                    >
                      <Users
                        style={{
                          cursor: 'pointer',
                        }}
                      />{' '}
                      {lang.teams.responsibleForCase}
                    </Button>
                    <Button
                      block
                      onClick={() => {
                        setSettingsOpen(!settingsOpen)
                      }}
                      theme="light"
                      style={dropdownStyle}
                    >
                      <Settings
                        style={{
                          cursor: 'pointer',
                        }}
                      />{' '}
                      {lang.cases.settings}
                    </Button>
                    <Button
                      block
                      onClick={toggleMergeModalOpen}
                      theme="light"
                      style={dropdownStyle}
                    >
                      <GitMerge
                        style={{
                          cursor: 'pointer',
                        }}
                      />{' '}
                      {lang.mergeCases.mergeBtn}
                    </Button>
                    <Button
                      block
                      onClick={toggleAssociatedCaseModalOpen}
                      theme="light"
                      style={dropdownStyle}
                    >
                      <Hexagon
                        style={{
                          cursor: 'pointer',
                        }}
                      />{' '}
                      {lang.associateCases.associateCase}
                    </Button>
                    <Button
                      block
                      onClick={() => {
                        setSelectedUltimateCreditorId(
                          singleCase.ultimateCreditorId,
                        )
                        setUltimateCreditorOpen(true)
                      }}
                      theme="light"
                      style={dropdownStyle}
                    >
                      <Briefcase
                        style={{
                          cursor: 'pointer',
                        }}
                      />{' '}
                      {lang.ultimateCreditors.manageUltimateCreditor}
                    </Button>
                    {meRole && isAdmin(meRole) && (
                      <Button
                        block
                        onClick={toggleDeleteModal}
                        theme="light"
                        style={dropdownStyle}
                      >
                        <Trash2
                          style={{
                            cursor: 'pointer',
                          }}
                        />{' '}
                        {lang.cases.deleteCase}
                      </Button>
                    )}
                    <ExclusiveContent>
                      <Button
                        block
                        onClick={() => indexMutation.mutate()}
                        theme="light"
                        style={dropdownStyle}
                        loading={indexMutation.isLoading}
                      >
                        <Zap /> Add case to the search index
                      </Button>
                    </ExclusiveContent>
                  </ButtonDrop>
                </MenuItem>
              )}
              {blameLogsOpen && (
                <Modal
                  onToggle={() => setBlameLogsOpen(!blameLogsOpen)}
                  header={lang.blameLogs.blameLog}
                  maxWidth="960px"
                >
                  {singleCase && (
                    <BlameLogsContent
                      refType={BlameLogRefType.Case}
                      refId={singleCase.caseId}
                    />
                  )}
                </Modal>
              )}

              {singleCase && settingsOpen && (
                <Modal
                  onToggle={() => setSettingsOpen(!settingsOpen)}
                  header={lang.cases.settings}
                  maxWidth="960px"
                >
                  {singleCase && <CaseSettings caseId={singleCase.caseId} />}
                </Modal>
              )}

              {ultimateCreditorOpen && (
                <Modal
                  onToggle={() =>
                    setUltimateCreditorOpen(!ultimateCreditorOpen)
                  }
                  header={lang.ultimateCreditors.manageUltimateCreditor}
                  maxWidth="450px"
                >
                  {ultimateCreditors == null ? (
                    h.isError(ultimateCreditorsLoadable) ? (
                      <Errors errors={[lang.errors.loadingError]} />
                    ) : (
                      <PlaceholderLoader height="34px" />
                    )
                  ) : ultimateCreditors.length === 0 ? (
                    <p>{lang.ultimateCreditors.noUltimateCreditors}</p>
                  ) : (
                    <>
                      <Select
                        label={lang.ultimateCreditors.ultimateCreditor}
                        options={ultimateCreditorOptions(
                          lang.ultimateCreditors.none,
                          ultimateCreditors || [],
                        )}
                        selected={
                          selectedUltimateCreditorId
                            ? [selectedUltimateCreditorId]
                            : []
                        }
                        onChange={(selected: string[]) =>
                          setSelectedUltimateCreditorId(selected[0] || null)
                        }
                      />
                      <Button
                        block
                        loading={ultimateCreditorMutation.isLoading}
                        disabled={
                          selectedUltimateCreditorId ===
                          singleCase.ultimateCreditorId
                        }
                        onClick={() => {
                          ultimateCreditorMutation.mutate(
                            selectedUltimateCreditorId,
                          )
                        }}
                        style={{ marginTop: '15px' }}
                      >
                        {selectedUltimateCreditorId
                          ? lang.ultimateCreditors.setUltimateCreditor
                          : lang.ultimateCreditors.removeUltimateCreditor}
                      </Button>
                    </>
                  )}
                </Modal>
              )}

              {responsibleOpen && (
                <Modal
                  onToggle={() => setresponsibleOpen(!responsibleOpen)}
                  header={lang.teams.responsibleForCase}
                  maxWidth="650px"
                >
                  <Well>
                    <SearchResponsible
                      onChange={(change) => {
                        responsibleMutation.mutate(change)
                      }}
                      responsible={responsible}
                    />
                  </Well>
                </Modal>
              )}

              {mergeModalOpen && singleCase && (
                <Modal
                  header={lang.mergeCases.header}
                  onToggle={toggleMergeModalOpen}
                  maxWidth="500px"
                >
                  <p>{lang.mergeCases.explanation}</p>
                  {toCase ? (
                    <SelectedCase>
                      <CasePreview caseId={toCase.id}></CasePreview>
                      <X
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setToCase(null)
                        }}
                      ></X>
                    </SelectedCase>
                  ) : (
                    <Search
                      placeholder={lang.navigation.search}
                      removeWrapper
                      entity={Entity.Case}
                      creditorId={singleCase.creditorId}
                      onSelect={setToCase}
                    />
                  )}
                  <Button
                    disabled={!(toCase && toCase.id)}
                    block
                    loading={mergeMutation.isLoading}
                    onClick={() => {
                      if (toCase?.id) {
                        mergeMutation.mutate(toCase.id)
                      }
                    }}
                  >
                    {lang.mergeCases.mergeBtn}
                  </Button>
                </Modal>
              )}

              {associatedCaseModalOpen && singleCase && (
                <AssociatedCaseModal
                  case={singleCase}
                  onClose={() => setAssociatedCaseModalOpen(false)}
                  reloadCase={reloadCase}
                />
              )}

              {deleteModalOpen && (
                <Modal
                  maxWidth="400px"
                  header={lang.cases.sureYouWantToDeleteCase}
                  onToggle={toggleDeleteModal}
                >
                  <Button
                    theme="dangerous"
                    loading={deleteMutation.isLoading}
                    size="small"
                    block={true}
                    onClick={() => {
                      deleteMutation.mutate()
                    }}
                  >
                    {lang.cases.deleteCase}
                  </Button>
                </Modal>
              )}
            </>
          )
        }
      />
    </Col>
  )
}

export default CaseHeader

const SelectedCase = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1em;
  margin-bottom: 1em;
  background: #ddd;
  border-radius: 3px;
`
